/**
 * PasteStyleToggle (iter-50) — per-book switch that lets authors opt
 * into "keep original styling" when pasting from Google Docs / Word.
 * Strict (default) is the safest choice; permissive keeps colours,
 * font sizes and alignment end-to-end but exports may look off if the
 * pasted styling clashes with the selected style pack.
 */
import React, { useState } from "react";
import { Palette, Shield } from "lucide-react";
import { toast } from "sonner";

export function PasteStyleToggle({ book, patch }) {
  const mode = (book?.paste_style_mode || "strict").toLowerCase();
  const [busy, setBusy] = useState(false);

  const setMode = async (next) => {
    if (next === mode) return;
    setBusy(true);
    try {
      await patch({ paste_style_mode: next });
      toast.success(next === "permissive"
        ? "Paste styling: original colours & sizes kept"
        : "Paste styling: strict cleanup restored");
    } catch (err) {
      toast.error(err?.message || "Could not update paste style");
    } finally { setBusy(false); }
  };

  return (
    <section className="mt-4 border border-[color:var(--border)] bg-white px-4 py-3 flex items-center justify-between gap-4"
             data-testid="paste-style-toggle">
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-muted)]">
          § Paste from Google Docs / Word
        </p>
        <p className="text-xs text-[color:var(--text-muted)] mt-1">
          Choose how much of the original styling to keep when authors
          paste content into chapters. Applies to every chapter in this
          book.
        </p>
      </div>
      <div className="flex items-stretch border border-[color:var(--border)] shrink-0">
        <button
          type="button" onClick={()=>setMode("strict")} disabled={busy}
          className={"px-3 py-1.5 text-xs inline-flex items-center gap-1 " +
                     (mode === "strict"
                       ? "bg-[color:var(--primary)] text-white"
                       : "bg-white hover:bg-[color:var(--canvas)]")}
          data-testid="paste-style-strict">
          <Shield className="w-3 h-3"/> Strict clean
        </button>
        <button
          type="button" onClick={()=>setMode("permissive")} disabled={busy}
          className={"px-3 py-1.5 text-xs inline-flex items-center gap-1 border-l border-[color:var(--border)] " +
                     (mode === "permissive"
                       ? "bg-[color:var(--primary)] text-white"
                       : "bg-white hover:bg-[color:var(--canvas)]")}
          data-testid="paste-style-permissive">
          <Palette className="w-3 h-3"/> Keep original styling
        </button>
      </div>
    </section>
  );
}
